import useDeleteUserOrganization from 'hooks/use-delete-user-organization'
import { useGetOrganizationUsers } from 'hooks/use-get-organization-users'
import useUpdateUserOrganization from 'hooks/use-update-user-organization'
import { UserOrganization } from 'interfaces/user-organization'
import { UserWithAppleId } from 'interfaces/user-with-apple-id'
import { useEffect, useState } from 'react'
import { Card, Button, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import toast from 'react-hot-toast'
import { useHistory, Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from 'index'

const BLUE_USER_MESSAGE = "Can't remove users with a Linq Blue number assigned."

type RemoveMemberButtonProps = {
  userOrganization: UserOrganization
  blue: boolean
  onRemove: (e: React.MouseEvent, userOrg: UserOrganization) => void
}

const RemoveMemberButton = ({
  userOrganization,
  blue,
  onRemove,
}: RemoveMemberButtonProps) => {
  const button = (
    <Button
      variant="danger"
      size="sm"
      className="p-1"
      title={blue ? undefined : 'Delete member from organization'}
      disabled={blue}
      onClick={(e) => {
        e.stopPropagation()
        if (blue) return
        onRemove(e, userOrganization)
      }}
    >
      Remove
    </Button>
  )

  if (!blue) return button

  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`blue-user-remove-${userOrganization.user.id}`}>
          {BLUE_USER_MESSAGE}
        </Tooltip>
      }
    >
      <span className="d-inline-block">{button}</span>
    </OverlayTrigger>
  )
}

interface OrganizationMembersTableProps {
  handleAddUserToOrganizationClicked: () => void
  orgId: string
  usersWithAppleIdsData?: {
    data: {
      users: UserWithAppleId[]
    }
  }
}
const OrganizationMembersTable = ({
  handleAddUserToOrganizationClicked,
  orgId,
  usersWithAppleIdsData,
}: OrganizationMembersTableProps) => {
  const history = useHistory()
  const [roleFilterValue, setRoleFilterValue] = useState('all')
  const [userOrganizations, setUserOrganizations] = useState<
    UserOrganization[]
  >([])

  const currentUser = useSelector((state: RootState) => state.user.currentUser)

  const {
    data: userOrganizationsData,
    isFetching: isUserOrganizationsFetching,
  } = useGetOrganizationUsers(orgId)
  const usersWithAppleIds = usersWithAppleIdsData?.data?.users
  const blueUserIds = new Set(
    (usersWithAppleIds || [])
      .filter((u) => u.apple_id?.active === true)
      .map((u) => String(u.id))
  )

  const { mutate: updateUserOrganization } = useUpdateUserOrganization(orgId)

  const { mutate: deleteUserOrganization } = useDeleteUserOrganization(orgId)

  // Check if current user is admin of this organization
  const isCurrentUserAdmin =
    (currentUser as any)?.user_organizations?.[0]?.is_admin || false
  useEffect(() => {
    if (userOrganizationsData?.data?.user_organizations) {
      setUserOrganizations(userOrganizationsData?.data?.user_organizations)
    }
  }, [userOrganizationsData?.data?.user_organizations])
  const handleBulkUploadClick = () => {
    history.push(`/organizations/${orgId}/bulk-uploads`)
  }

  const handleRemoveUserClicked = (
    clickEvent: any,
    userOrg: UserOrganization
  ) => {
    clickEvent.stopPropagation()
    if (
      window.confirm(
        `Are you sure you want to remove the user '${userOrg.user.name}' from the organization?`
      ) === true
    ) {
      deleteUserOrganization(
        { userOrg: userOrg },
        {
          onSuccess: () => {
            toast(`${userOrg.user.name} removed from the organization`, {
              icon: `🚪`,
            })
          },
        }
      )
    }
  }

  const handleFilterRole = ({ value }: { value: string }) => {
    setRoleFilterValue(value)
    if (value === 'all') {
      setUserOrganizations(
        userOrganizationsData?.data?.user_organizations || []
      )
    } else if (value === 'admin_only') {
      const onlyAdminUsers =
        userOrganizationsData?.data?.user_organizations.filter(
          (userOrganization) => userOrganization.is_admin === true
        )
      setUserOrganizations(onlyAdminUsers || [])
    }
  }

  const handleUpdateUserRole = (userOrg: UserOrganization) => {
    const isAdmin = userOrg.is_admin
    const newRole = !isAdmin
    const confirmMessage = `Are you sure you want to make the user '${
      userOrg.user.name
    }' become a ${newRole ? 'admin' : 'member'}?`

    if (window.confirm(confirmMessage)) {
      updateUserOrganization(
        {
          isAdmin: newRole,
          userOrgId: userOrg.id,
        },
        {
          onSuccess: () => {
            toast(
              `${userOrg.user.name} updated to ${newRole ? 'admin' : 'member'}`,
              {
                icon: `👍`,
              }
            )
          },
        }
      )
    }
  }
  return (
    <Card className="mt-4">
      {isUserOrganizationsFetching ? (
        <div className="m-2">Loading...</div>
      ) : (
        <>
          <Card.Header>
            <h4>Organization Members</h4>
          </Card.Header>
          <Card.Body>
            <Button
              onClick={handleAddUserToOrganizationClicked}
              variant="success"
              className="mb-3 me-3"
            >
              Add User to Organization
            </Button>
            <Button className="mb-3" onClick={handleBulkUploadClick}>
              Bulk Uploads
            </Button>
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                border: '1px solid #495057',
                borderRadius: '0.375rem',
              }}
            >
              <Table
                striped
                bordered
                hover
                variant="dark"
                className="mb-0"
                style={{ borderRadius: '0' }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 100,
                    backgroundColor: '#343a40',
                  }}
                >
                  <tr>
                    <th style={{ borderTop: 'none' }}>#</th>
                    <th style={{ borderTop: 'none' }}>Name</th>
                    <th style={{ borderTop: 'none' }}>Email</th>
                    <th style={{ borderTop: 'none' }}> Account Phone Number</th>
                    <th style={{ borderTop: 'none' }}>
                      <select
                        value={roleFilterValue}
                        onChange={(e) => handleFilterRole(e.target)}
                      >
                        <option value="all">All Role</option>
                        <option value="admin_only">Admin Only</option>
                      </select>
                    </th>
                    <th style={{ borderTop: 'none' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {userOrganizations &&
                    userOrganizations.map((userOrganization, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>
                          <Link
                            to={`/user/${userOrganization.user.id}`}
                            style={{
                              color: '#4da6ff',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                            }}
                          >
                            {userOrganization.user.name}
                          </Link>
                        </td>
                        <td>{userOrganization.user.email}</td>
                        <td>{userOrganization.user.phone_number || 'N/A'}</td>
                        <td>
                          <select
                            value={
                              userOrganization.is_admin ? 'admin' : 'member'
                            }
                            onChange={(e) => {
                              e.stopPropagation()
                              if (!isCurrentUserAdmin) {
                                toast.error('Only admins can change user roles')
                                return
                              }
                              const newRole = e.target.value === 'admin'
                              if (newRole !== userOrganization.is_admin) {
                                handleUpdateUserRole(userOrganization)
                              }
                            }}
                            className="form-select form-select-sm"
                            style={{ width: 'auto', display: 'inline-block' }}
                          >
                            <option value="member">Member</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td>
                          <RemoveMemberButton
                            userOrganization={userOrganization}
                            blue={blueUserIds.has(
                              String(userOrganization.user.id)
                            )}
                            onRemove={handleRemoveUserClicked}
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </>
      )}
    </Card>
  )
}

export default OrganizationMembersTable
